import { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useSearchParams, useNavigate } from "react-router-dom"
import { Dropdown, Flex, Button } from "antd"
import { useTranslation } from "react-i18next"
import { EllipsisIcon } from "icons"
import DataTable from "ui/DataTable"
import { getHeaders } from "./headers"
import { useDispatch, useSelector } from "react-redux"
import CustomPagination from "components/common/CustomPagination"
import Avatar from "components/common/Avatar"
import { Paragraph } from "ui/Typography"
import moment from "moment"
import { REQUEST_TYPES, PAYMENT_STATUS } from "../constants"
import CancelBookingModal from "../BookingActions/CancelBookingModal"
import { guideBookingStatus } from "store/bookings"
import tripImage from "assets/imagesV2/favicon_men.png"
import { toaster } from "components/common/Toast"
import { routes } from "router"
import BookingConfirmationModal from "ui/BookingConfirmationModal"
import StatusTag from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import WaiverStatus from "./WaiverStatus"
import { displayPaymentStatus } from "ui/Booking/Details/utils"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"

const BookingTable = ({
  page = 1,
  pageSize = 10,
  sortKey,
  sortOrder,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  selectedRowKeys,
  setSelectedRowKeys,
  fetchBookings
}) => {
  const { t } = useTranslation()
  const { items, metadata } = useSelector((store) => store.bookings)
  const [searchParams] = useSearchParams()
  const type = searchParams.get("type")
  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelBooking, setCancelBooking] = useState(null)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false)
  const [bookingConfirmStatus, setBookingConfirmStatus] = useState("")
  const [booking, setBooking] = useState(null)
  const { hasPermission } = usePermissions()

  const canCreateBooking = hasPermission(MODULES.BOOKINGS, ACTIONS.CREATE)

  const getMenuItems = (booking) => {
    if (!booking.can_edit) {
      return []
    }

    return [
      {
        key: "edit",
        label: t("bookings.edit"),
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation()
          navigate(routes.guideBookingEditPath({ id: booking.uuid }))
        }
      },
      {
        key: "cancel",
        label: t("bookings.cancel"),
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation()
          const bookings = {
            id: booking?.id,
            image: booking?.trip_image[0]?.placeholder_url || tripImage,
            title: booking.trip_title,
            booking_id: booking.booking_id,
            booked_on: booking?.create_at ? moment(booking?.create_at).format("MM/DD/YYYY, hh:mm A") : "",
            uuid: booking?.uuid,
            client_name: booking?.full_name,
            client_email: booking?.email
          }
          setCancelBooking(bookings)
          setCancelModalOpen(true)
        }
      }
    ]
  }

  const renderActions = (booking, type, t, handleBookingConfirmBtnClick, getMenuItems, payment_status, canCreateBooking) => {
    let actionContent
    if (type === REQUEST_TYPES.requested) {
      actionContent = (
        <>
          <Button
            color="primary"
            variant="solid"
            style={{ whiteSpace: "nowrap" }}
            disabled={booking?.is_expired}
            onClick={(e) => {
              e.stopPropagation()
              if (booking?.is_expired) return
              handleBookingConfirmBtnClick(booking, "accept")
            }}
          >
            {t("bookings.accept")}
          </Button>
          <Button
            color="primary"
            variant="outlined"
            style={{ whiteSpace: "nowrap" }}
            disabled={booking?.is_expired}
            onClick={(e) => {
              e.stopPropagation()
              if (booking?.is_expired) return
              handleBookingConfirmBtnClick(booking, "decline")
            }}
          >
            {t("bookings.decline")}
          </Button>
        </>
      )
    } else if (type === REQUEST_TYPES.upcoming) {
      actionContent = (
        <Dropdown
          menu={{
            items: getMenuItems(booking)
          }}
          trigger={["click"]}
          placement="bottomRight"
          disabled={payment_status === PAYMENT_STATUS.partial_refund}
        >
          <button
            type="button"
            className="ml-auto"
            aria-label="More actions"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: payment_status === PAYMENT_STATUS.partial_refund ? "not-allowed" : "pointer"
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={payment_status === PAYMENT_STATUS.partial_refund}
          >
            <EllipsisIcon fill="#525455" width={20} height={20} />
          </button>
        </Dropdown>
      )
    } else {
      actionContent = (
        <Button
          type="link"
          color="primary"
          style={{ whiteSpace: "nowrap" }}
          disabled={!canCreateBooking}
          onClick={(e) => {
            e.stopPropagation()
            if (!canCreateBooking) return
            navigate(routes.guideBookingBookAgainPath({ id: booking?.uuid }))
          }}
        >
          Book Again
        </Button>
      )
    }
    return (
      <Flex gap={8} className="w-full" style={{ flexWrap: "nowrap" }}>
        {actionContent}
      </Flex>
    )
  }

  const handleBookingConfirmBtnClick = (booking, status) => {
    setIsConfirmationModalVisible(true)
    setBookingConfirmStatus(status)
    setBooking(booking)
  }

  const handleConfirmBooking = async () => {
    setIsConfirmationModalVisible(false)
    try {
      const { uuid } = booking || {}
      await dispatch(guideBookingStatus(uuid, bookingConfirmStatus === "accept" ? "booked" : "canceled"))
      toaster.success({ title: "Booking status updated successfully" })
      fetchBookings(false)
    } catch (err) {
      console.error("Error updating booking status:", err)
    }
  }

  const dataSource = useMemo(() => {
    return items?.map((booking) => {
      const { id, experience_date, experience_time, payment_status, duration_type, total_guest_count, signed_waiver_count, is_waiver_attached } = booking || {}
      const bookingStatus = type
      const isMultiDay = duration_type === "multi_day"

      return {
        key: id,
        booked_date: isMultiDay
          ? `${experience_date || ""}, ${experience_time?.start_time || ""}`
          : `${experience_date || ""}, ${experience_time?.start_time || ""} - ${experience_time?.end_time || ""}`,
        client_name: (
          <Flex gap={16} align="center" className="ellipsis">
            <Avatar user={booking.avatar} size={44} border={false} fit={false} />
            <Paragraph ellipsis={{ rows: 1 }} title={booking?.full_name} style={{ width: "120px", margin: 0 }}>
              {booking?.full_name}
            </Paragraph>
          </Flex>
        ),
        experience: booking?.trip_title || "",
        total_amount: booking?.total_amount || 0,
        payment_status: (
          <Flex gap={8} align="center">
            <StatusTag status={displayPaymentStatus(payment_status)} bookingStatus={bookingStatus} />
            <WaiverStatus isWaiverAttached={is_waiver_attached} totalGuestCount={total_guest_count} signedWaiverCount={signed_waiver_count} />
          </Flex>
        ),
        created_at: booking?.create_at ? moment(booking?.create_at).format("MM/DD/YYYY, hh:mm A") : "",
        booking_id: booking?.booking_id,
        actions: renderActions(booking, type, t, handleBookingConfirmBtnClick, getMenuItems, payment_status, canCreateBooking)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, type, canCreateBooking])

  const handleBookingRowClick = (key) => {
    const booking = items?.find((item) => item?.id === key)
    navigate(routes.guideBookingDetailsPath({ id: booking?.uuid }))
  }

  return (
    <>
      <div className="overflow-auto">
        <DataTable
          columns={getHeaders({
            field: sortKey,
            order: sortOrder,
            type: type
          })}
          dataSource={dataSource}
          pagination={false}
          rowClickKey="key"
          onChange={onSortChange}
          rowSelection={false}
          rowKey={(record) => record.key}
          onRowClick={handleBookingRowClick}
        />
      </div>
      {metadata?.total_results > 10 && (
        <div className="mb-m">
          <CustomPagination
            metadata={metadata}
            pageSize={pageSize}
            total={metadata?.total_results || 0}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
      {cancelModalOpen && cancelBooking && (
        <CancelBookingModal
          open={cancelModalOpen}
          bookings={[cancelBooking]}
          onConfirm={() => {
            setCancelModalOpen(false)
            fetchBookings(false)
          }}
          onCancel={() => setCancelModalOpen(false)}
        />
      )}
      {isConfirmationModalVisible && (
        <BookingConfirmationModal
          open={isConfirmationModalVisible}
          handleCancel={() => setIsConfirmationModalVisible(false)}
          handleConfirm={handleConfirmBooking}
          clientName={booking?.full_name}
          tripDate={moment(booking?.create_at).format("MM/DD/YYYY, hh:mm A")}
          bookingConfirmStatus={bookingConfirmStatus}
        />
      )}
    </>
  )
}

BookingTable.propTypes = {
  page: PropTypes.number,
  pageSize: PropTypes.number,
  sortKey: PropTypes.string,
  sortOrder: PropTypes.string,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onSortChange: PropTypes.func,
  selectedRowKeys: PropTypes.array.isRequired,
  setSelectedRowKeys: PropTypes.func.isRequired,
  fetchBookings: PropTypes.func
}

export default BookingTable
